import { useRouteMatch } from "react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { toast } from "react-toastify";

import { Modal, ModalKind } from "../modal/Modal";
import { PanelWidth } from "../panel/Panel";
import { PaymentStatus, usePaymentDataLazyQuery, usePaymentStatusQuery } from "../../graphql/schema";
import { Loader } from "../loader/Loader";
import { CopyToClipboard } from "../copy-to-clipboard/CopyToClipBoard";
import { IconBind } from "../icon/IconBind";
import { Countdown } from "../countdown/Countdown";

import styles from "./top-up-modal.module.scss";

interface RouteParams {
  id: string;
}

export const PaymentView = () => {
  const { params } = useRouteMatch<RouteParams>();
  const { t } = useTranslation();
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [pollingDisabled, setPollingDisabled] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showPaymentFailed, setShowPaymentFailed] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [showCountdown, setShowCountdown] = useState(true);
  const paymentId = null;
  const transactionId = params.id;

  const { data, startPolling, stopPolling } = usePaymentStatusQuery({
    variables: { transactionId, paymentId },
    skip: !transactionId,
    fetchPolicy: "network-only",
    pollInterval: 3000,
  });

  const paymentResult = data?.payments.fetch;
  const paymentStatus = paymentResult?.status ?? undefined;

  const [loadPaymentData, { data: paymentData, loading, error }] = usePaymentDataLazyQuery({
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paymentResult === undefined || paymentStatus === undefined || pollingDisabled) {
      return;
    }

    // return payment result response
    if (paymentResult === null) {
      setPollingDisabled(true);
      stopPolling();
    }

    if (paymentResult?.status === PaymentStatus.SUCCESS) {
      setPollingDisabled(true);
      setShowPaymentSuccess(true);
      stopPolling();
      // If saving card is available
    } else if (paymentResult?.status === PaymentStatus.INVALID) {
      setPollingDisabled(true);
      setShowPaymentFailed(true);
      stopPolling();
    } else if (paymentResult?.status === PaymentStatus.PENDING && !isPolling) {
      loadPaymentData({
        variables: { id: transactionId },
      });
      setIsPolling(true);
      setPollingDisabled(false);
      startPolling(5000);
    }
  }, [
    paymentResult,
    paymentStatus,
    stopPolling,
    pollingDisabled,
    startPolling,
    isPolling,
    loadPaymentData,
    transactionId,
  ]);

  useEffect(() => {
    let isMounted = true;

    if (!paymentData || !paymentData.getPaymentLinkData) {
      setQrCodeUrl(null);
      setQrError(null);
      return () => {
        isMounted = false;
      };
    }

    const requestUri = paymentData.getPaymentLinkData.payment.requestUri;

    QRCode.toDataURL(requestUri, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 320,
    })
      .then((value: string) => {
        if (!isMounted) {
          return;
        }

        setQrCodeUrl(value);
        setQrError(null);
      })
      .catch((qrGenerationError: unknown) => {
        if (!isMounted) {
          return;
        }

        console.error("Failed to generate QR code", qrGenerationError);
        setQrCodeUrl(null);
        setQrError("We could not generate a QR code for this link.");
      });

    return () => {
      isMounted = false;
    };
  }, [paymentData]);

  const paymentLink = paymentData?.getPaymentLinkData ?? null;

  const decodedRequestUri = useMemo(() => {
    if (!paymentLink) {
      return null;
    }

    try {
      return decodeURIComponent(paymentLink.payment.requestUri);
    } catch (error) {
      return paymentLink.payment.requestUri;
    }
  }, [paymentLink]);

  const renderQRInfo = (paymentLink: any) => (
    <>
      <section className="payment-card details">
        <div className={styles["deposit-box"]}>
          <section className="payment-card qr-section">
            <div>
              <h2>Scan &amp; pay</h2>
              <p>Use your preferred wallet to scan this QR code and confirm the payment details automatically.</p>
            </div>

            <div className={styles["qr-wrapper"]}>
              {qrCodeUrl ? (
                <img src={qrCodeUrl} alt="QR code" className={styles["qr-image"]} />
              ) : qrError ? (
                <div className="qr-error">
                  {/* <AlertTriangle className="icon" /> */}
                  {qrError}
                </div>
              ) : (
                <Loader className="icon spinner" />
              )}
            </div>

            <dl className="payment-info-grid">
              <InfoBox
                label={`Amount due (${paymentLink.payment.token.replace("_", "-")})`}
                value={paymentLink.payment.amount}
              />
            </dl>
          </section>
          <div className={styles["address-box"]}>
            <CopyToClipboard
              iconColor
              copyText={paymentLink.payment.depositAddress ?? ""}
              onClick={() => {
                toast.success(t("Address copied"));
              }}
              icon={<IconBind fill="#ff871c" width={18} height={18} />}
            >
              {paymentLink.payment.depositAddress}
            </CopyToClipboard>
          </div>
          <p>Send the exact amount to this address from your wallet.</p>
          {showCountdown && (
            <Countdown
              onCountDownEnd={() => setShowCountdown(false)}
              showTimeLeft
              showDays={false}
              showHours={false}
              targetDate={new Date(
                new Date(paymentLink.createdAt.replace(" ", "T") + "Z").getTime() + 30 * 60 * 1000,
              ).toISOString()}
            />
          )}
          {!showCountdown && (
            <p style={{ color: "red", padding: "10px 0", textAlign: "center", fontWeight: "bold", fontSize: 20 }}>
              Payment is expired.
            </p>
          )}
          <p className="hint" style={{ margin: "10px 0" }}>
            <strong style={{ fontSize: "10px" }}>
              Always verify the address and amount before completing the transfer. Payments on-chain cannot be reversed
              once sent.
            </strong>
          </p>
        </div>
      </section>
    </>
  );

  return (
    <Modal
      isOpen
      shouldCloseOnOverlayClick={false}
      kind={ModalKind.SECONDARY}
      close={() => {
        if (paymentLink?.payment?.token === "TWR" || paymentLink?.payment?.token === "DAGS") {
          window.location.href = "/account/dag-community#deposit";
        } else {
          window.location.href = "/account/payment-methods";
        }
      }}
      panelProps={{
        title: t("Payment"),
        width: PanelWidth.SMALL,
        hideImage: true,
        // caption: t("First select the amount that you want to add and then select your preferred payment method."),
        captionClassName: styles["title-caption"],
      }}
    >
      <>
        {/* Loading */}
        {loading && (
          <section className="payment-card loading">
            <Loader className="icon spinner" />
            <div>
              <h1>Loading payment link…</h1>
              <p>We’re preparing the payment details for you.</p>
            </div>
          </section>
        )}

        {/* Error */}
        {error && (
          <section className="payment-card error">
            {/* <AlertTriangle className="icon error-icon" /> */}
            <h1>{"We could not load this payment link."}</h1>
            <p>Please verify the link or contact the merchant for assistance.</p>

            <a href="/" className="btn btn-outline">
              Return home
            </a>
          </section>
        )}

        {showPaymentSuccess && (
          <div
            className="confirmed-badge"
            style={{
              textAlign: "center",
            }}
          >
            {/* <CheckCircle2 className="icon" /> */}
            <h1
              style={{
                color: "#00d383",
                fontWeight: "bold",
              }}
            >
              Payment is confirmed
            </h1>
            <p>You may close this window.</p>
          </div>
        )}

        {showPaymentFailed && (
          <div
            className="confirmed-badge"
            style={{
              textAlign: "center",
            }}
          >
            {/* <CheckCircle2 className="icon" /> */}
            <h1
              style={{
                color: "#d30000",
                fontWeight: "bold",
              }}
            >
              Payment failed
            </h1>
          </div>
        )}

        {/* Payment link loaded */}
        {paymentLink && !showPaymentSuccess && !showPaymentFailed && (
          <div className="payment-grid">{renderQRInfo(paymentLink)}</div>
        )}
      </>
    </Modal>
  );
};

function InfoBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="info-box">
      <p>
        <span className="label" style={{ marginRight: "15px" }}>
          {label}
        </span>
        {value}
      </p>
    </div>
  );
}
